import {
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { selectGridNameByIdMap } from "~/features/andon/andon.slice";
import { AndonBoardWorkstationSelection } from "~/features/andon/andon.types";
import { resolveWorkstationSelections } from "~/features/andon/andon.utils";
import { useAndonWorkstationsByFcIds } from "~/hooks/useAndonWorkstationsByFcIds";
import { groupArrayByKey } from "~/lib/helpers";
import { useGetAllFulfillmentCentersQuery } from "~/redux/warehouse/fulfillmentCenter.hooks";
import { AndonWorkstation } from "~/types/api";

type WorkstationSelectorProps = {
  selectedWorkstationIds: string[];
  setNewIdsCb: (newIds: string[]) => void;
  onFcIdsChange?: (fcIds: string[]) => void;
  getSelectionsRef?: React.MutableRefObject<
    (() => AndonBoardWorkstationSelection[]) | null
  >;
};

export function WorkstationSelector(props: WorkstationSelectorProps) {
  const {
    selectedWorkstationIds,
    setNewIdsCb,
    onFcIdsChange,
    getSelectionsRef
  } = props;

  const { data: allFcs = [] } = useGetAllFulfillmentCentersQuery();
  const siteFulfillmentCenterId = useAppSelector(
    (state) => state.workstations.siteFulfillmentCenterId
  );
  const [selectedFcIds, setSelectedFcIds] = useState<string[]>([]);
  const { workstations: fetchedWorkstations = [] } =
    useAndonWorkstationsByFcIds(selectedFcIds, { poll: false });

  const fcWorkstationsById = useMemo(() => {
    const workstationsByFcId: Record<string, AndonWorkstation[]> = {};
    for (const workstation of fetchedWorkstations) {
      if (!workstation.fulfillmentCenterId) continue;
      (workstationsByFcId[workstation.fulfillmentCenterId] ??= []).push(
        workstation
      );
    }
    return workstationsByFcId;
  }, [fetchedWorkstations]);

  const gridNameByIdMap = useAppSelector(selectGridNameByIdMap);
  const { t } = useTranslation();

  // Binds current picker state to resolveWorkstationSelections for getSelectionsRef.
  // The util is pure; the parent reads FC-scoped selections on confirm via the ref.
  const workstationSelectionsGetter = useCallback(
    () =>
      resolveWorkstationSelections({
        workstationIds: selectedWorkstationIds,
        fetchedWorkstations,
        fcWorkstationsById,
        selectedFcIds,
        siteFulfillmentCenterId
      }),
    [
      selectedWorkstationIds,
      fetchedWorkstations,
      fcWorkstationsById,
      selectedFcIds,
      siteFulfillmentCenterId
    ]
  );

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (getSelectionsRef) {
      getSelectionsRef.current = workstationSelectionsGetter;
    }
  }, [getSelectionsRef, workstationSelectionsGetter]);

  const handleFcToggle = (
    _event: React.MouseEvent<HTMLElement>,
    newFcIds: string[]
  ) => {
    const removedFcIds = selectedFcIds.filter(
      (fulfillmentCenterId) => !newFcIds.includes(fulfillmentCenterId)
    );
    setSelectedFcIds(newFcIds);
    onFcIdsChange?.(newFcIds);

    if (removedFcIds.length === 0) return;

    const removedWorkstationIds = new Set(
      removedFcIds.flatMap((fulfillmentCenterId) =>
        (fcWorkstationsById[fulfillmentCenterId] ?? []).map(
          (workstation) => workstation.workstationId
        )
      )
    );
    if (removedWorkstationIds.size === 0) return;

    setNewIdsCb(
      selectedWorkstationIds.filter(
        (workstationId) => !removedWorkstationIds.has(workstationId)
      )
    );
  };

  const groupedWorkstations = groupArrayByKey(
    fetchedWorkstations,
    (ws) => ws.autostoreGridId
  );

  const toggleWorkstation = (id: string) => {
    if (selectedWorkstationIds.includes(id)) {
      setNewIdsCb(selectedWorkstationIds.filter((wid) => wid !== id));
    } else {
      setNewIdsCb([...selectedWorkstationIds, id]);
    }
  };

  const selectGrid = (gridWorkstations: typeof fetchedWorkstations) => {
    const gridIds = gridWorkstations.map((ws) => ws.workstationId);
    const allSelected = gridIds.every((id) =>
      selectedWorkstationIds.includes(id)
    );

    if (allSelected) {
      setNewIdsCb(selectedWorkstationIds.filter((id) => !gridIds.includes(id)));
    } else {
      const newIds = new Set([...selectedWorkstationIds, ...gridIds]);
      setNewIdsCb(Array.from(newIds));
    }
  };

  return (
    <Stack spacing={2} py={2}>
      {allFcs.length > 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {t("fulfillment centers")}
          </Typography>
          <ToggleButtonGroup
            value={selectedFcIds}
            onChange={handleFcToggle}
            size="small"
            sx={{ flexWrap: "wrap", gap: 0.5 }}
          >
            {allFcs.map((fc) => (
              <ToggleButton
                key={fc.fulfillmentCenterId}
                value={fc.fulfillmentCenterId}
                sx={{ textTransform: "none", px: 2 }}
              >
                {fc.abbreviation || fc.name}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {selectedFcIds.length === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              {t("select fc label")}
            </Typography>
          )}
        </Box>
      )}

      <Stack
        spacing={2}
        sx={{
          maxHeight: "500px",
          overflowY: "auto",
          px: 1
        }}
      >
        {Object.entries(groupedWorkstations).map(([gridId, workstations]) => {
          const allFromGridSelected = workstations.every((ws) =>
            selectedWorkstationIds.includes(ws.workstationId)
          );

          return (
            <Box
              key={gridId}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 2
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {gridNameByIdMap[gridId] || `Grid ${gridId.slice(0, 8)}...`}
                </Typography>
                {allFromGridSelected ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => selectGrid(workstations)}
                  >
                    {t("deselect grid")}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="subtle"
                    color="primary"
                    onClick={() => selectGrid(workstations)}
                  >
                    {t("select grid")}
                  </Button>
                )}
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 1
                }}
              >
                {workstations
                  .sort((a, b) => a.deviceId.localeCompare(b.deviceId))
                  .map((workstation) => {
                    const isSelected = selectedWorkstationIds.includes(
                      workstation.workstationId
                    );
                    const isActive = workstation.status === "Active";

                    const chipSx = {
                      width: "100%",
                      justifyContent: "flex-start",
                      borderColor: isActive ? "success.main" : undefined,
                      borderWidth: isActive && !isSelected ? 2 : 1,
                      "&:hover": {
                        backgroundColor: isSelected
                          ? "primary.dark"
                          : "action.hover"
                      }
                    };

                    return isSelected ? (
                      <Chip
                        key={workstation.workstationId}
                        label={workstation.deviceId}
                        onClick={() =>
                          toggleWorkstation(workstation.workstationId)
                        }
                        color="1"
                        variant="filled"
                        sx={chipSx}
                      />
                    ) : (
                      <Chip
                        key={workstation.workstationId}
                        label={workstation.deviceId}
                        onClick={() =>
                          toggleWorkstation(workstation.workstationId)
                        }
                        variant="outlined"
                        sx={chipSx}
                      />
                    );
                  })}
              </Box>
            </Box>
          );
        })}
      </Stack>

      <Typography variant="body2" color="text.secondary" textAlign="center">
        {selectedWorkstationIds.length} of {fetchedWorkstations.length}{" "}
        {t("workstations selected")}
      </Typography>
    </Stack>
  );
}

export default WorkstationSelector;
